import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Download, Play, Search, MapPin, Sparkles, CheckCircle2, ChevronRight, FileText, Info, Award, HelpCircle, Trophy } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

// 12 Marker Locations on India Map (X, Y percentages calculated mathematically for @svg-maps/india)
const markersData = [
  { id: 'delhi', name: 'Delhi / NCR', count: 4, top: '29.5%', left: '30.8%', state: 'Delhi', schools: ['Amity International School', 'DLF Public School', 'Mount Carmel School, Dwarka', 'Darbari Lal DAV Model School'] },
  { id: 'haldwani', name: 'Haldwani', count: 1, top: '27.5%', left: '38.3%', state: 'Uttarakhand', schools: ['Doon Convent School'] },
  { id: 'shirpur', name: 'Shirpur', count: 1, top: '53.5%', left: '23.2%', state: 'Maharashtra', schools: ['R.C. Patel Secondary School & Jr. College'] },
  { id: 'mumbai', name: 'Mumbai', count: 2, top: '61.1%', left: '16.6%', state: 'Maharashtra', schools: ['Mind Factory / Science Kidz (Team A)', 'Mind Factory / Science Kidz (Team B)'] },
  { id: 'pune', name: 'Pune', count: 1, top: '62.9%', left: '19.8%', state: 'Maharashtra', schools: ["The Bishop's Co-Ed School Undri"] },
  { id: 'goa', name: 'Goa', count: 1, top: '73.5%', left: '20.7%', state: 'Goa', schools: ['Saraswat Vidyalaya'] },
  { id: 'puttur', name: 'Puttur', count: 1, top: '82.1%', left: '24.3%', state: 'Karnataka', schools: ['Vivekananda Central School (CBSE)'] },
  { id: 'malappuram', name: 'Malappuram', count: 1, top: '87.5%', left: '27.1%', state: 'Kerala', schools: ['PMSAPTHSS Kakkove'] },
  { id: 'bengaluru', name: 'Bengaluru', count: 4, top: '81.3%', left: '32.0%', state: 'Karnataka', schools: ['Govt High School 18th Cross Malleshwaram (Team A)', 'Govt High School 18th Cross Malleshwaram (Team B)', 'AOL School', 'Vivekananda School (VVSP)'] },
  { id: 'kurnool', name: 'Kurnool', count: 1, top: '71.8%', left: '33.5%', state: 'Andhra Pradesh', schools: ['Ravindra Vidya Niketan'] },
  { id: 'hyderabad', name: 'Hyderabad', count: 2, top: '66.7%', left: '34.9%', state: 'Telangana', schools: ['Delhi Public School Nacharam', 'Army Public School Bolarum'] },
  { id: 'pedanadipadu', name: 'Pedanadipadu', count: 1, top: '70.7%', left: '40.8%', state: 'Andhra Pradesh', schools: ['Zilla Parishad High School, Pedanadipadu'] },
];

const schoolsData = [
  { id: 1, name: 'Mind Factory / Science Kidz (Team A)', city: 'Mumbai', state: 'Maharashtra', mapId: 'mumbai' },
  { id: 2, name: 'Mind Factory / Science Kidz (Team B)', city: 'Mumbai', state: 'Maharashtra', mapId: 'mumbai' },
  { id: 3, name: 'Delhi Public School Nacharam', city: 'Hyderabad', state: 'Telangana', mapId: 'hyderabad' },
  { id: 4, name: 'Ravindra Vidya Niketan', city: 'Kurnool', state: 'Andhra Pradesh', mapId: 'kurnool' },
  { id: 5, name: 'The Bishop\'s Co-Ed School Undri', city: 'Pune', state: 'Maharashtra', mapId: 'pune' },
  { id: 6, name: 'Doon Convent School', city: 'Haldwani', state: 'Uttarakhand', mapId: 'haldwani' },
  { id: 7, name: 'Amity International School', city: 'Noida / NCR', state: 'Delhi/NCR', mapId: 'delhi' },
  { id: 8, name: 'Army Public School Bolarum', city: 'Secunderabad / Hyderabad', state: 'Telangana', mapId: 'hyderabad' },
  { id: 9, name: 'PMSAPTHSS Kakkove', city: 'Malappuram', state: 'Kerala', mapId: 'malappuram' },
  { id: 10, name: 'Darbari Lal DAV Model School', city: 'Delhi', state: 'Delhi/NCR', mapId: 'delhi' },
  { id: 11, name: 'Vivekananda Central School (CBSE)', city: 'Puttur', state: 'Karnataka', mapId: 'puttur' },
  { id: 12, name: 'DLF Public School', city: 'Ghaziabad / NCR', state: 'Delhi/NCR', mapId: 'delhi' },
  { id: 13, name: 'Saraswat Vidyalaya', city: 'Mapusa', state: 'Goa', mapId: 'goa' },
  { id: 14, name: 'Govt High School 18th Cross Malleshwaram (Team A)', city: 'Bengaluru', state: 'Karnataka', mapId: 'bengaluru' },
  { id: 15, name: 'Govt High School 18th Cross Malleshwaram (Team B)', city: 'Bengaluru', state: 'Karnataka', mapId: 'bengaluru' },
  { id: 16, name: 'R.C. Patel Secondary School & Jr. College', city: 'Shirpur', state: 'Maharashtra', mapId: 'shirpur' },
  { id: 17, name: 'Mount Carmel School, Dwarka', city: 'Delhi', state: 'Delhi/NCR', mapId: 'delhi' },
  { id: 18, name: 'Zilla Parishad High School, Pedanadipadu', city: 'Pedanadipadu', state: 'Andhra Pradesh', mapId: 'pedanadipadu' },
  { id: 19, name: 'AOL School', city: 'Bengaluru', state: 'Karnataka', mapId: 'bengaluru' },
  { id: 20, name: 'Vivekananda School (VVSP)', city: 'Bengaluru', state: 'Karnataka', mapId: 'bengaluru' },
];

const selectionStages = [
  { stage: 'Applications received', count: 132, note: 'Schools from across India applied', color: 'bg-pragmatic/20 text-deepwalnut border-pragmatic' },
  { stage: 'Shortlisted', count: 57, note: 'Reviewed for technical eligibility and merit', color: 'bg-heavyrain/20 text-deepwalnut border-heavyrain' },
  { stage: 'Interviewed', count: 24, note: 'Shortlisted teams assessed through online technical interviews', color: 'bg-richolive/20 text-white border-richolive bg-richolive/90' },
  { stage: 'Final teams selected', count: 20, note: 'Competed at BYKM 2026, Bengaluru — June 20, 2026', color: 'bg-dwarfpony text-white border-dwarfpony shadow-lg shadow-dwarfpony/25' },
];

const timelineMilestones = [
  { title: 'Applications open', detail: 'Teams from schools across India registered for FGC Nationals / BYKM 2026', status: 'Completed', date: 'Jan-Feb 2026' },
  { title: '20 teams selected — pan India', detail: '132 applications → 57 shortlisted → 24 interviewed → 20 finalists selected', status: 'Completed', date: 'March 2026' },
  { title: 'Bharatiya Yantra Khel Mahotsav (BYKM 2026)', detail: 'June 20, 2026 · Beagles Basketball Stadium, Bengaluru · 20 national finalist teams competed in 10 qualification & 7 playoff matches before 3,000+ attendees', status: 'Completed', date: 'June 20, 2026' },
  { title: 'Team India selected', detail: 'Team India 2026 selected to represent the nation at FIRST Global Challenge', status: 'Completed', date: 'June 20, 2026' },
  { title: 'FIRST Global Challenge — Incheon, South Korea', detail: 'Team India competes against 190+ countries on the global robotics stage', status: 'Upcoming', date: 'October 2026' },
];

const bykmAwardsData = [
  {
    title: "Vikram Sarabhai Award for National Excellence",
    category: "Overall Championship",
    winner: "TechInfinity",
    institution: "Saraswat Vidyalaya · Mapusa, Goa",
    citation: "Highest overall honour for excellence in robot performance, engineering, innovation, teamwork, documentation, and national spirit.",
    badgeColor: "bg-amber-500/10 text-amber-700 border-amber-500/20"
  },
  {
    title: "Robot Game Winner",
    category: "Competition Champion",
    winner: "Convergence India",
    institution: "Mind Factory / Science Kidz · Mumbai, Maharashtra",
    citation: "Grand Finale Winner with a top score of 280 in the Fire & Rescue robot game.",
    badgeColor: "bg-red-500/10 text-red-700 border-red-500/20"
  },
  {
    title: "New Technology Experience (NTE) Winner",
    category: "Innovation Champion",
    winner: "Bot-Gineers",
    institution: "Mind Factory / Science Kidz · Mumbai, Maharashtra",
    citation: "Innovative AI & Robotics solution, real-world application, and working prototype.",
    badgeColor: "bg-purple-500/10 text-purple-700 border-purple-500/20"
  },
  {
    title: "Robot Game Finalist",
    category: "Runner-Up",
    winner: "TechInfinity",
    institution: "Saraswat Vidyalaya · Mapusa, Goa",
    citation: "Advanced to the Grand Finale with a score of 225, demonstrating strong competitive performance.",
    badgeColor: "bg-blue-500/10 text-blue-700 border-blue-500/20"
  },
  {
    title: "NTE Finalist",
    category: "Runner-Up",
    winner: "TechInfinity",
    institution: "Saraswat Vidyalaya · Mapusa, Goa",
    citation: "Recognised for a well-designed innovative working solution with detailed problem-solving.",
    badgeColor: "bg-blue-500/10 text-blue-700 border-blue-500/20"
  },
  {
    title: "M. Visvesvaraya Award for Engineering Design",
    category: "Engineering Excellence",
    winner: "Quantum Crew",
    institution: "Delhi Public School Nacharam · Hyderabad, Telangana",
    citation: "Recognised for impressive engineering design approach reflecting the legacy of Sir M. Visvesvaraya.",
    badgeColor: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20"
  },
  {
    title: "Satish Dhawan Award for Innovation in Engineering",
    category: "Innovation",
    winner: "Team Dhurandhars of Viksit Bharat",
    institution: "DLF Public School, Sahibabad · Ghaziabad, Uttar Pradesh",
    citation: "Demonstrated innovation, engineering creativity, and a strong problem-solving mindset.",
    badgeColor: "bg-orange-500/10 text-orange-700 border-orange-500/20"
  },
  {
    title: "Kalpana Chawla Award for Courageous Achievement",
    category: "Resilience & Courage",
    winner: "Puttur Technovators",
    institution: "Vivekananda Central School (CBSE) · Puttur, Karnataka",
    citation: "Displayed courage, resilience, and the spirit of achievement reflecting Kalpana Chawla.",
    badgeColor: "bg-pink-500/10 text-pink-700 border-pink-500/20"
  },
  {
    title: "Tessy Thomas Award for Engineering Documentation",
    category: "Documentation",
    winner: "Bot-Gineers",
    institution: "Mind Factory / Science Kidz · Mumbai, Maharashtra",
    citation: "Strong engineering documentation and presentation demonstrating thorough engineering logs.",
    badgeColor: "bg-teal-500/10 text-teal-700 border-teal-500/20"
  },
  {
    title: "Sudha Murthy Enthusiasm Award",
    category: "Team Spirit",
    winner: "Cosmic Coders",
    institution: "VVS Sardar Patel High School · Bengaluru, Karnataka",
    citation: "Inspiring energy, active participation, and positive team presence throughout the championship.",
    badgeColor: "bg-indigo-500/10 text-indigo-700 border-indigo-500/20"
  },
  {
    title: "Outstanding Mentor Award",
    category: "Mentorship",
    winner: "Ramesh Ranjit",
    institution: "Ravindra Vidya Niketan / Dynamic Girls · Kurnool, Andhra Pradesh",
    citation: "Outstanding dedication, guidance, and commitment to supporting young innovators.",
    badgeColor: "bg-amber-500/10 text-amber-700 border-amber-500/20"
  },
  {
    title: "Judges Award — Technical",
    category: "Technical Distinction",
    winner: "Team Bishop's Undri",
    institution: "The Bishop's Co-Ed School Undri · Pune, Maharashtra",
    citation: "Exceptional technical capability, clean build, and sound mechanical engineering.",
    badgeColor: "bg-slate-500/10 text-slate-700 border-slate-500/20"
  },
  {
    title: "Judges Award — Non-Technical",
    category: "Community & Spirit",
    winner: "Inferno",
    institution: "Govt High School, 18th Cross · Bengaluru, Karnataka",
    citation: "Outstanding non-technical presentation, team spirit, and community engagement.",
    badgeColor: "bg-slate-500/10 text-slate-700 border-slate-500/20"
  },
  {
    title: "Social Media Award",
    category: "Outreach & Media",
    winner: "Team Forge",
    institution: "Govt High School, 18th Cross · Bengaluru, Karnataka",
    citation: "Effectively showcased their BYKM journey and engaged the community online.",
    badgeColor: "bg-rose-500/10 text-rose-700 border-rose-500/20"
  }
];

const FGCNationals2026Page = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const [svgContent, setSvgContent] = useState(null);
  const directoryRef = useRef(null);
  const schoolCardsRefs = useRef({});

  useEffect(() => {
    // Sourced from public CDN for geographical accuracy
    fetch('https://cdn.jsdelivr.net/npm/@svg-maps/india@1.0.1/india.svg')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch map');
        return res.text();
      })
      .then((text) => {
        const cleaned = text.replace(/<\?xml[^>]*\?>/g, '').replace(/<!DOCTYPE[^>]*>/g, '');
        setSvgContent(cleaned);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  // Filter schools based on search query or selected map marker
  const filteredSchools = useMemo(() => {
    return schoolsData.filter((school) => {
      const matchesSearch =
        school.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        school.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
        school.state.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesMarker = selectedMarker ? school.mapId === selectedMarker : true;

      return matchesSearch && matchesMarker;
    });
  }, [searchQuery, selectedMarker]);

  const handleMarkerClick = (markerId) => {
    if (selectedMarker === markerId) {
      // Toggle off
      setSelectedMarker(null);
    } else {
      setSelectedMarker(markerId);
      // Scroll list into view if on mobile
      if (window.innerWidth < 768 && directoryRef.current) {
        directoryRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleSchoolCardClick = (mapId) => {
    setSelectedMarker(mapId);
    // Smoothly scroll the map container into view if clicked from the card
    const mapElement = document.getElementById('india-interactive-map');
    if (mapElement) {
      mapElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="fgc-nationals-2026-page">
      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-cream via-pragmatic/10 to-richolive/5">
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pragmatic/15 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-richolive/10 rounded-full blur-3xl" />
            {/* Grid Pattern overlay */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSg2MCkgMCBMIDAgMCAwIDYwIiBmaWxsPSJub25lIiBzdHJva2U9IiNjM2E2OTMyMCIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              <span className="inline-block text-dwarfpony text-xs font-bold tracking-widest uppercase mb-4 px-4 py-1.5 bg-dwarfpony/10 rounded-full border border-dwarfpony/20 animate-pulse">
                FGC India Nationals 2026
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-midnight mb-6 leading-tight tracking-tight">
                Bharatiya Yantra Khel Mahotsav <span className="text-transparent bg-clip-text bg-gradient-to-r from-dwarfpony to-dwarfpony-dark">(BYKM) 2026</span>
              </h1>
              <p className="text-lg md:text-xl text-deepwalnut leading-relaxed max-w-3xl mx-auto mb-12">
                20 elite robotics teams from across India compete in Bengaluru. One will earn the honor to represent the country at the FIRST Global Challenge — an international robotics olympiad with 190+ nations
              </p>

              {/* Stats Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                <div className="bg-cream/80 backdrop-blur-md border border-pragmatic/30 rounded-2xl p-6 shadow-md hover:shadow-xl hover:border-dwarfpony/30 transition-all duration-300 transform hover:-translate-y-1">
                  <div className="text-5xl font-black text-dwarfpony mb-2">20</div>
                  <div className="text-sm font-semibold text-deepwalnut uppercase tracking-wide">Teams Selected</div>
                </div>
                <div className="bg-cream/80 backdrop-blur-md border border-pragmatic/30 rounded-2xl p-6 shadow-md hover:shadow-xl hover:border-dwarfpony/30 transition-all duration-300 transform hover:-translate-y-1">
                  <div className="text-5xl font-black text-richolive mb-2">10+</div>
                  <div className="text-sm font-semibold text-deepwalnut uppercase tracking-wide">States Represented</div>
                </div>
                <div className="bg-cream/80 backdrop-blur-md border border-pragmatic/30 rounded-2xl p-6 shadow-md hover:shadow-xl hover:border-dwarfpony/30 transition-all duration-300 transform hover:-translate-y-1">
                  <div className="text-5xl font-black text-midnight mb-2">1</div>
                  <div className="text-sm font-semibold text-deepwalnut uppercase tracking-wide">Team to Korea</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BYKM 2026 Official Awards & Honors Section */}
        <section className="py-20 bg-cream border-t border-pragmatic/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-flex items-center gap-2 text-dwarfpony text-xs font-bold uppercase tracking-widest px-4 py-1.5 bg-dwarfpony/10 rounded-full border border-dwarfpony/20">
                <Trophy size={14} className="text-dwarfpony" />
                BYKM 2026 National Awards
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-deepwalnut mt-4">
                BYKM 2026 Awards & Honors
              </h2>
              <p className="text-heavyrain mt-2 text-base">
                Official awards presented at the Bharatiya Yantra Khel Mahotsav 2026 National Finals in Bengaluru.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bykmAwardsData.map((award, idx) => (
                <div
                  key={idx}
                  className="bg-cream-dark border border-pragmatic/30 rounded-2xl p-6 shadow-sm hover:shadow-lg hover:border-dwarfpony/30 transition-all duration-300 flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${award.badgeColor}`}>
                        {award.category}
                      </span>
                      <Trophy size={18} className="text-dwarfpony flex-shrink-0" />
                    </div>
                    <h3 className="text-lg font-bold text-deepwalnut mb-2 leading-snug">
                      {award.title}
                    </h3>
                    <div className="mb-4">
                      <span className="text-sm font-extrabold text-midnight block">
                        🏆 {award.winner}
                      </span>
                      <span className="text-xs text-heavyrain font-medium block mt-0.5">
                        {award.institution}
                      </span>
                    </div>
                    <p className="text-xs text-heavyrain leading-relaxed border-t border-pragmatic/20 pt-3">
                      {award.citation}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Video Placeholder / Player */}
        <section className="py-16 bg-cream border-t border-pragmatic/20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-deepwalnut">Road to the Global Stage</h2>
              <p className="text-heavyrain mt-2">Watch the highlights of the journey, training, and competition preparing our students for the international stage.</p>
            </div>

            <div className="relative aspect-video rounded-3xl overflow-hidden shadow-2xl border-4 border-cream-dark bg-midnight">
              {isPlayingVideo ? (
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/3YZaYp0ZeEg?autoplay=1"
                  title="FGC India Nationals 2026 Highlights"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              ) : (
                <div className="relative w-full h-full cursor-pointer group" onClick={() => setIsPlayingVideo(true)}>
                  {/* Cover Placeholder overlay */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-t from-midnight/80 via-midnight/40 to-transparent z-10">
                    <div className="w-20 h-20 rounded-full bg-dwarfpony hover:bg-dwarfpony-dark flex items-center justify-center cursor-pointer shadow-lg shadow-dwarfpony/30 group-hover:scale-110 transition-all duration-300">
                      <Play className="text-white fill-current ml-1" size={32} />
                    </div>
                    <h3 className="text-white font-bold text-xl mt-6">FGC India Nationals 2026 Highlights</h3>
                    <p className="text-pragmatic text-sm mt-2">Watch the official event video</p>
                  </div>
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                    style={{ backgroundImage: "url('https://img.youtube.com/vi/3YZaYp0ZeEg/maxresdefault.jpg')" }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Selection Process Section */}
        <section className="py-20 bg-cream-dark border-t border-pragmatic/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-dwarfpony text-xs font-bold uppercase tracking-widest px-3 py-1 bg-dwarfpony/10 rounded-full">
                Selection Process
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-deepwalnut mt-4">
                How we got to 20 Teams
              </h2>
              <p className="text-heavyrain mt-2">
                Every team at FGC Nationals 2026 earned their spot through a rigorous multi-stage selection process spanning across months.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {selectionStages.map((stage, idx) => (
                <div
                  key={idx}
                  className="bg-cream border border-pragmatic/30 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all relative overflow-hidden group"
                >
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-pragmatic group-hover:bg-dwarfpony transition-all" />
                  <div className="text-heavyrain text-xs font-bold uppercase tracking-wider mb-2">Stage 0{idx + 1}</div>
                  <h3 className="text-lg font-bold text-deepwalnut mb-3">{stage.stage}</h3>
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-4xl font-black text-midnight">{stage.count}</span>
                    <span className="text-heavyrain text-xs font-medium uppercase">Schools</span>
                  </div>
                  <p className="text-sm text-heavyrain leading-relaxed">{stage.note}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Interactive India Map & Teams Directory */}
        <section id="india-interactive-map" className="py-20 bg-cream border-t border-pragmatic/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-dwarfpony text-xs font-bold uppercase tracking-widest px-3 py-1 bg-dwarfpony/10 rounded-full">
                Pan-India Representation
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-deepwalnut mt-4">
                20 Teams Across 10+ States
              </h2>
              <p className="text-heavyrain mt-2">
                Click a marker on the map to filter the school directory, or search the directory to highlight and locate the team.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              {/* Interactive Vector Map Container */}
              <div className="lg:col-span-6 bg-cream-dark border border-pragmatic/40 rounded-3xl p-6 shadow-lg relative flex flex-col items-center">
                {/* SVG Outline Map of India */}
                <div className="relative w-full max-w-[420px] aspect-[612/696] mt-8 select-none">
                  {svgContent ? (

                    <div 
                      dangerouslySetInnerHTML={{ __html: svgContent }} 
                      className="w-full h-full [&_svg]:w-full [&_svg]:h-full [&_path]:fill-cream [&_path]:stroke-pragmatic/50 [&_path]:stroke-[1.2] [&_path]:transition-all [&_path]:duration-300 hover:[&_path]:fill-pragmatic/10 hover:[&_path]:stroke-dwarfpony/60 cursor-pointer"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full min-h-[300px] text-heavyrain text-sm font-medium">
                      Loading Map...
                    </div>
                  )}


                  {/* HTML absolute overlay markers */}
                  <TooltipProvider delayDuration={150}>
                    {markersData.map((marker) => {
                      const isSelected = selectedMarker === marker.id;
                      return (
                        <div
                          key={marker.id}
                          className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer z-20 group"
                          style={{ top: marker.top, left: marker.left }}
                          onClick={() => handleMarkerClick(marker.id)}
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="relative flex items-center justify-center">
                                {/* Ripple circle */}
                                <span className={`absolute inline-flex h-7 w-7 rounded-full opacity-75 ${
                                  isSelected ? 'bg-dwarfpony animate-ping' : 'bg-richolive group-hover:animate-ping'
                                }`} />
                                {/* Solid center marker */}
                                <span className={`relative rounded-full flex items-center justify-center font-bold text-[10px] text-white border transition-all ${
                                  isSelected 
                                    ? 'bg-dwarfpony border-dwarfpony-dark h-6 w-6 scale-110 shadow-lg shadow-dwarfpony/45' 
                                    : 'bg-richolive/90 border-richolive h-5.5 w-5.5 shadow-sm group-hover:bg-dwarfpony group-hover:scale-105'
                                }`}>
                                  {marker.count}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="bg-midnight border border-richolive/40 text-cream p-3 max-w-[260px] rounded-xl shadow-xl">
                              <div className="font-bold text-sm text-pragmatic">{marker.name}</div>
                              <div className="text-xs text-heavyrain-light font-medium mt-0.5">{marker.count} {marker.count > 1 ? 'teams' : 'team'} • {marker.state}</div>
                              <ul className="list-disc list-inside mt-2 text-[11px] text-heavyrain space-y-1 border-t border-richolive/40 pt-1.5">
                                {marker.schools.map((school, sIdx) => (
                                  <li key={sIdx} className="truncate">{school}</li>
                                ))}
                              </ul>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      );
                    })}
                  </TooltipProvider>
                </div>

                {/* Reset button */}
                {selectedMarker && (
                  <div className="w-full flex items-center justify-end border-t border-pragmatic/30 pt-4 mt-8 text-xs">
                    <Button 
                      variant="ghost" 
                      onClick={() => setSelectedMarker(null)}
                      className="text-dwarfpony hover:text-dwarfpony-dark text-xs p-0 h-auto hover:bg-transparent font-bold underline"
                    >
                      Show All Teams
                    </Button>
                  </div>
                )}
              </div>

              {/* Directory List Container */}
              <div ref={directoryRef} className="lg:col-span-6 space-y-6">
                <div className="bg-cream-dark border border-pragmatic/30 rounded-2xl p-6 shadow-md">
                  <h3 className="text-xl font-bold text-deepwalnut mb-2 flex items-center gap-2">
                    <Sparkles className="text-dwarfpony w-5 h-5" />
                    School Directory
                  </h3>
                  <p className="text-heavyrain text-sm mb-6">
                    Search by school name, city, or state. Selecting a city maps where they come from.
                  </p>

                  <div className="relative">
                    <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-heavyrain w-4.5 h-4.5" />
                    <Input
                      type="text"
                      placeholder="Search schools, cities, or states..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-6 border-pragmatic/30 bg-cream placeholder:text-heavyrain text-deepwalnut focus:ring-dwarfpony focus:border-dwarfpony rounded-xl"
                    />
                  </div>
                </div>

                {/* Directory Cards Grid */}
                <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                  {filteredSchools.length > 0 ? (
                    filteredSchools.map((school, index) => {
                      const isHighlighted = selectedMarker === school.mapId;
                      return (
                        <div
                          key={school.id}
                          ref={el => schoolCardsRefs.current[school.id] = el}
                          onClick={() => handleSchoolCardClick(school.mapId)}
                          className={`bg-cream border rounded-xl p-5 cursor-pointer transition-all duration-300 transform flex items-start justify-between gap-4 ${
                            isHighlighted
                              ? 'border-dwarfpony bg-gradient-to-br from-pragmatic/10 to-cream shadow-md -translate-y-0.5'
                              : 'border-pragmatic/20 hover:border-dwarfpony/30 hover:shadow'
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <span className="text-[10px] font-bold text-heavyrain uppercase tracking-wider">Team #{index + 1}</span>
                            <h4 className="font-bold text-deepwalnut text-base mt-0.5 truncate">{school.name}</h4>
                            <div className="flex items-center gap-2 text-xs text-heavyrain mt-2">
                              <MapPin size={13} className="text-dwarfpony flex-shrink-0" />
                              <span>{school.city}, {school.state}</span>
                            </div>
                          </div>
                          <div className="flex-shrink-0 flex items-center justify-center">
                            <ChevronRight size={18} className={`transition-all ${isHighlighted ? 'text-dwarfpony translate-x-1' : 'text-heavyrain'}`} />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-12 border border-dashed border-pragmatic/40 rounded-2xl bg-cream-dark">
                      <p className="text-heavyrain font-medium text-sm">No schools match your search query.</p>
                      <Button 
                        variant="link" 
                        onClick={() => { setSearchQuery(''); setSelectedMarker(null); }}
                        className="text-dwarfpony font-bold text-xs mt-2"
                      >
                        Reset Search Filters
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-20 bg-cream-dark border-t border-pragmatic/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-dwarfpony text-xs font-bold uppercase tracking-widest px-3 py-1 bg-dwarfpony/10 rounded-full">
                Roadmap
              </span>
              <h2 className="text-3xl font-extrabold text-deepwalnut mt-4">
                Milestones & Timeline
              </h2>
              <p className="text-heavyrain mt-2">
                Follow the path of our robotics champions from registration to the global stage in Korea.
              </p>
            </div>

            {/* Timeline Tree */}
            <div className="relative border-l border-pragmatic/50 ml-4 md:ml-6 space-y-12">
              {timelineMilestones.map((milestone, idx) => {
                const isCompleted = milestone.status === 'Completed';
                const isCurrent = milestone.status === 'Coming up';
                return (
                  <div key={idx} className="relative pl-8 md:pl-10 group">
                    {/* Circle Pin */}
                    <span className={`absolute left-0 transform -translate-x-1/2 flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all ${
                      isCompleted 
                        ? 'bg-richolive border-richolive text-white' 
                        : isCurrent 
                          ? 'bg-dwarfpony border-dwarfpony text-white animate-pulse'
                          : 'bg-cream border-pragmatic/60 text-heavyrain'
                    }`} style={{ top: '0.25rem' }}>
                      {isCompleted ? (
                        <CheckCircle2 size={16} />
                      ) : (
                        <span className="text-xs font-bold">{idx + 1}</span>
                      )}
                    </span>

                    {/* Content Card */}
                    <div className="bg-cream border border-pragmatic/30 rounded-2xl p-6 shadow-sm group-hover:shadow hover:border-dwarfpony/30 transition-all duration-300">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                        <h3 className="font-extrabold text-deepwalnut text-lg">{milestone.title}</h3>
                        <Badge className={`w-fit font-bold text-xs uppercase px-2 py-0.5 rounded-full ${
                          isCompleted 
                            ? 'bg-richolive/10 text-richolive border border-richolive/20' 
                            : isCurrent 
                              ? 'bg-dwarfpony text-white shadow shadow-dwarfpony/25'
                              : 'bg-heavyrain/10 text-heavyrain border border-heavyrain/20'
                        }`}>
                          {milestone.status}
                        </Badge>
                      </div>
                      <span className="text-xs font-semibold text-heavyrain tracking-wider block mb-3">{milestone.date}</span>
                      <p className="text-heavyrain text-sm leading-relaxed">{milestone.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Downloads / Official Documents Section */}
        <section className="py-20 bg-cream border-t border-pragmatic/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-dwarfpony text-xs font-bold uppercase tracking-widest px-3 py-1 bg-dwarfpony/10 rounded-full">
                Resources
              </span>
              <h2 className="text-3xl font-extrabold text-deepwalnut mt-4">
                Official Rules & Challenge Documents
              </h2>
              <p className="text-heavyrain mt-2">
                Download the official rulebook and challenges documents for FGC India Nationals 2026.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Card 1: Game Book */}
              <div className="bg-cream-dark border border-pragmatic/30 rounded-3xl p-8 hover:shadow-xl hover:border-dwarfpony/30 transition-all duration-300 relative group flex flex-col justify-between min-h-[250px]">
                <div>
                  <div className="w-12 h-12 rounded-xl bg-dwarfpony/10 flex items-center justify-center mb-6 border border-dwarfpony/20 text-dwarfpony">
                    <FileText size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-deepwalnut mb-3">FGC Nationals 2026 — Game Book</h3>
                  <p className="text-heavyrain text-sm leading-relaxed mb-6">
                    The official game manual for the Fire & Rescue challenge. Covers game objectives, field setup, scoring rules, and robotics technical specifications.
                  </p>
                </div>
                <div className="border-t border-pragmatic/10 pt-4">
                  <a 
                    href="/docs/FGC%20Fire%20%26%20Rescue%20GAME%20MANUAL.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block w-full"
                  >
                    <Button 
                      className="w-full bg-gradient-to-r from-dwarfpony to-dwarfpony-dark hover:from-dwarfpony-dark hover:to-dwarfpony text-white font-semibold shadow-lg shadow-dwarfpony/20 gap-2 rounded-xl"
                    >
                      <Download size={16} />
                      Download Manual
                    </Button>
                  </a>
                </div>
              </div>

              {/* Card 2: Media Challenge */}
              <div className="bg-cream-dark border border-pragmatic/30 rounded-3xl p-8 hover:shadow-xl hover:border-dwarfpony/30 transition-all duration-300 relative group flex flex-col justify-between min-h-[250px]">
                <div>
                  <div className="w-12 h-12 rounded-xl bg-richolive/10 flex items-center justify-center mb-6 border border-richolive/20 text-richolive">
                    <Award size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-deepwalnut mb-3">Media Challenge Guidelines</h3>
                  <p className="text-heavyrain text-sm leading-relaxed mb-6">
                    Details of this year's media and social branding challenges — evaluating team communication, outreach campaigns, and online presence.
                  </p>
                </div>
                <div className="border-t border-pragmatic/10 pt-4">
                  <a 
                    href="/docs/SM%20challenge.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block w-full"
                  >
                    <Button 
                      className="w-full bg-gradient-to-r from-richolive to-richolive-dark hover:from-richolive-dark hover:to-richolive text-white font-semibold shadow-lg shadow-richolive/20 gap-2 rounded-xl"
                    >
                      <Download size={16} />
                      Download Guidelines
                    </Button>
                  </a>
                </div>
              </div>

              {/* Card 3: BYKM 2026 */}
              <div className="bg-cream-dark border border-pragmatic/30 rounded-3xl p-8 hover:shadow-xl hover:border-dwarfpony/30 transition-all duration-300 relative group flex flex-col justify-between min-h-[250px]">
                <div>
                  <div className="w-12 h-12 rounded-xl bg-midnight/10 flex items-center justify-center mb-6 border border-midnight/20 text-midnight">
                    <Info size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-deepwalnut mb-3">Bharatiya Yantra Khel Mahotsav (BYKM 2026)</h3>
                  <p className="text-heavyrain text-sm leading-relaxed mb-6">
                    The Official National Trials for identifying the student representatives of India for the Olympics of Robotics. Evaluates teams across Track A (Robotics Challenge) and Track B (NTE Challenge).
                  </p>
                </div>
                <div className="flex flex-col gap-3 border-t border-pragmatic/10 pt-4 w-full">
                  <a 
                    href="/docs/BYKM%202026%20Document.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block w-full"
                  >
                    <Button 
                      className="w-full bg-gradient-to-r from-midnight to-midnight/80 hover:from-midnight/80 hover:to-midnight text-white font-semibold shadow-lg shadow-midnight/20 gap-2 rounded-xl"
                    >
                      <Download size={16} />
                      Download Framework
                    </Button>
                  </a>
                  <a 
                    href="/docs/BYKM%202026%20Presentation.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block w-full"
                  >
                    <Button 
                      className="w-full bg-gradient-to-r from-dwarfpony to-dwarfpony-dark hover:from-dwarfpony-dark hover:to-dwarfpony text-white font-semibold shadow-lg shadow-dwarfpony/20 gap-2 rounded-xl"
                    >
                      <Download size={16} />
                      Download Slides
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Support Banner / CTA */}
        <section className="py-20 bg-gradient-to-br from-midnight to-richolive-dark relative overflow-hidden">
          {/* Subtle glowing elements */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-dwarfpony/15 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-richolive/20 rounded-full blur-3xl" />

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6">
              Want to support Team India's journey?
            </h2>
            <p className="text-heavyrain-light text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
              Help us send these brilliant students from across India to represent our nation on the world stage in Korea.
            </p>
            <Link to="/donate">
              <Button className="bg-gradient-to-r from-dwarfpony to-dwarfpony-dark hover:from-dwarfpony-dark hover:to-dwarfpony text-white font-semibold px-8 py-6 text-lg shadow-xl shadow-dwarfpony/20 group rounded-xl">
                Donate Now
                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FGCNationals2026Page;
